import { Component } from '@theme/component';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} senderName
 * @property {HTMLInputElement} senderEmail
 * @property {HTMLInputElement} senderEmailConfirm
 * @property {HTMLInputElement} recipientName
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} recipientEmailConfirm
 * @property {HTMLTextAreaElement} recipientMessage
 * @property {HTMLInputElement} deliveryDate
 * @property {HTMLInputElement} [timezoneOffset]
 * @property {HTMLParagraphElement} [senderEmailConfirmError]
 * @property {HTMLParagraphElement} [recipientEmailConfirmError]
 * @property {HTMLSpanElement} [characterCount]
 */

const attributes = {
  HIDDEN: 'hidden',
  TEMPLATE: 'data-template',
};

const classes = {
  INPUT_ERROR: 'input-error',
};

/** @extends {Component<GiftCardRecipientFormRefs>} */
class GiftCardRecipientForm extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {{ destroy: () => void } | null} */
  #datePicker = null;

  requiredRefs = [
    'senderName',
    'senderEmail',
    'senderEmailConfirm',
    'recipientName',
    'recipientEmail',
    'recipientEmailConfirm',
    'recipientMessage',
    'deliveryDate',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initializeForm();
    this.#bindEmailValidation(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#datePicker?.destroy();
    this.#datePicker = null;
  }

  /** @param {AbortSignal} signal */
  #bindEmailValidation(signal) {
    this.#confirmEmail('senderEmail', 'senderEmailConfirm', 'senderEmailConfirmError', signal);
    this.#confirmEmail(
      'recipientEmail',
      'recipientEmailConfirm',
      'recipientEmailConfirmError',
      signal,
    );
    this.refs.recipientMessage.addEventListener('input', () => this.#updateCharacterCount(), {
      signal,
    });
  }

  /**
   * @param {string} emailRef
   * @param {string} confirmRef
   * @param {string} errorRef
   * @param {AbortSignal} signal
   */
  #confirmEmail(emailRef, confirmRef, errorRef, signal) {
    const validate = () => this.#validateEmailMatch(emailRef, confirmRef, errorRef);
    /** @type {HTMLInputElement} */ (this.refs[emailRef]).addEventListener('input', validate, {
      signal,
    });
    /** @type {HTMLInputElement} */ (this.refs[confirmRef]).addEventListener('input', validate, {
      signal,
    });
  }

  #initializeForm() {
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    this.#initDatePicker();
    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const template = this.refs.characterCount.getAttribute(attributes.TEMPLATE);

    if (!template) return;

    this.refs.characterCount.textContent = template
      .replace('[current]', currentLength.toString())
      .replace('[max]', this.refs.recipientMessage.maxLength.toString());

    this.refs.characterCount.toggleAttribute(attributes.HIDDEN, currentLength === 0);
  }

  #initDatePicker() {
    const today = new Date();
    const maxDate = new Date();
    // You can only schedule a gift card to be sent up to 90 days in the future.
    maxDate.setDate(today.getDate() + 90);

    this.#datePicker = /** @type {any} */ (window).flatpickr(this.refs.deliveryDate, {
      minDate: today,
      maxDate,
      disableMobile: true,
      dateFormat: Theme.translations?.recipient_form_date_format || 'd/m/Y',
    });
  }

  /**
   * @param {string} emailRef
   * @param {string} confirmRef
   * @param {string} errorRef
   */
  #validateEmailMatch(emailRef, confirmRef, errorRef) {
    const confirmField = /** @type {HTMLInputElement} */ (this.refs[confirmRef]);
    const emailField = /** @type {HTMLInputElement} */ (this.refs[emailRef]);
    const errorContainer = /** @type {HTMLElement} */ (this.refs[errorRef]);

    if (!confirmField || !emailField || !errorContainer) return;

    if (!emailField.value || !confirmField.value) {
      confirmField.setCustomValidity('');
      confirmField.classList.remove(classes.INPUT_ERROR);
      errorContainer.setAttribute(attributes.HIDDEN, '');

      return;
    }

    const isMatch = emailField.value === confirmField.value;
    const mismatchMessage =
      Theme.translations?.recipient_form_email_confirm_error || 'Email addresses do not match';

    confirmField.setCustomValidity(isMatch ? '' : mismatchMessage);
    confirmField.classList.toggle(classes.INPUT_ERROR, !isMatch);
    errorContainer.textContent = isMatch ? '' : mismatchMessage;
    errorContainer.toggleAttribute(attributes.HIDDEN, isMatch);
  }
}

customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
